import { computed, ref } from "vue";
import { defineStore } from "pinia";
import axios from "axios";
import network from "@/utils/network";
import { changeTheme, localFetch, newLogger } from "@/utils/util";
import { UI_THEMES } from "@/utils/const";

const AUTH_CONTEXT_STORAGE_KEY = "internal_portal_auth_context";

const endpoints = {
  login: "/api/authbro/auth/login",
  resetPassword: "/api/authbro/auth/password-recovery",
  confirmPasswordRecovery: "/api/authbro/auth/password-recovery/confirm",
  secondFactor: "/api/authbro/auth/mfa/verify",
  activate: "/api/authbro/auth/invitations/accept",
  logout: "/authbro/logout",
  logoutAll: "/authbro/full-logout",
  killSession: "/authbro/kill-session",
  myData: "/authbro/auth/sessions/mine",
  currentSession: "/authbro/auth/session",
};

const logger = newLogger("AuthSession");

const toJsonJwt = (auth) =>
  auth
    ? {
        sub: auth.userId,
        aud: auth.tenantIdentifier,
        email: auth.userEmail,
        tz: auth.timezone,
        fname: auth.userEmail,
        authorities: auth.privileges || [],
      }
    : {};

const normalizeAuthorities = (authorities) =>
  Array.from(
    new Set(
      (Array.isArray(authorities) ? authorities : [])
        .map((authority) =>
          typeof authority === "string" ? authority.trim() : ""
        )
        .filter(Boolean)
    )
  );

const flattenAuthorities = (authorities) =>
  authorities.flatMap((authority) =>
    Array.isArray(authority) ? flattenAuthorities(authority) : [authority]
  );

const parseAuthContext = (value) => {
  if (!value) {
    return null;
  }
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (_error) {
    return null;
  }
};

const resolveAuthResponse = (response) => {
  const auth = response?.data?.auth || response?.data;
  if (auth) {
    return auth;
  }
  throw response?.data || "No auth context in response!";
};

const persistAuthContext = (authContext) => {
  localStorage.setItem(
    AUTH_CONTEXT_STORAGE_KEY,
    authContext ? JSON.stringify(authContext) : ""
  );
};

const applyUserTheme = () => {
  const theme = localFetch("saved_ui_theme");
  if (theme !== undefined) {
    changeTheme(theme, false);
  } else {
    changeTheme(UI_THEMES.SYSTEM);
  }
};

export const useAuthSessionStore = defineStore("authSession", () => {
  const authContext = ref(
    parseAuthContext(localStorage.getItem(AUTH_CONTEXT_STORAGE_KEY))
  );
  const bootstrapped = ref(false);
  const reloginRequired = ref(false);
  const loginRedirect = ref("/");
  const sessionData = ref({});
  const isCurrentSession = ref(false);
  const selectedForecastYear = ref(undefined);

  const jsonJwt = computed(() => toJsonJwt(authContext.value));
  const isReady = computed(() => Boolean(authContext.value));
  const isLoggedIn = computed(() => isReady.value);
  const userId = computed(() =>
    isReady.value ? jsonJwt.value.sub : "UNKNOWN"
  );
  const isRoot = computed(() =>
    Boolean(isReady.value && jsonJwt.value.aud === "MASTER")
  );
  const displayName = computed(() =>
    isReady.value ? jsonJwt.value.fname : "ISMERETLEN"
  );
  const loggingRequired = computed(() =>
    isReady.value ? Boolean(jsonJwt.value.lgreq) : false
  );
  const workScheduleMode = computed(() =>
    isReady.value ? jsonJwt.value.wrksch : undefined
  );
  const avatar = computed(() =>
    isReady.value && jsonJwt.value.ava && jsonJwt.value.ava !== "null"
      ? jsonJwt.value.ava
      : null
  );
  const occupation = computed(() =>
    isReady.value ? jsonJwt.value.tit || null : null
  );
  const partnerId = computed(() =>
    isReady.value ? jsonJwt.value.ptnid || 0 : 0
  );
  const authorities = computed(() =>
    isReady.value ? normalizeAuthorities(jsonJwt.value.authorities) : []
  );
  const tenantId = computed(() => jsonJwt.value.aud);

  const timezone = computed(() => jsonJwt.value.tz || null);

  const selectedForecastYearResolved = computed(
    () =>
      selectedForecastYear.value ||
      (isReady.value ? jsonJwt.value.fcy : undefined)
  );

  const setAuthContext = (context) => {
    authContext.value = context && typeof context === "object" ? context : null;
    persistAuthContext(authContext.value);
  };

  const clearAuthContext = () => {
    authContext.value = null;
    persistAuthContext(null);
  };

  const setReadyStateFromAuth = (context) => {
    setAuthContext(context);
    bootstrapped.value = true;
    applyUserTheme();
  };

  const bootstrap = async () => {
    if (bootstrapped.value) {
      return isReady.value;
    }
    try {
      const http = network.createUnauthorizedAxiosInstance();
      const response = await http.get(endpoints.currentSession);
      setReadyStateFromAuth(resolveAuthResponse(response));
      bootstrapped.value = true;
      return true;
    } catch (_error) {
      boot(true);
      bootstrapped.value = true;
      return false;
    }
  };

  const refreshJwt = async () => bootstrap();

  const login = async (loginRequest) => {
    const response = await axios.post(endpoints.login, loginRequest, {
      withCredentials: true,
      headers: {
        "X-Tenant-Id": loginRequest.tenant,
      },
    });
    if (response?.data?.status === "MFA_REQUIRED") {
      throw response.data;
    }
    if (response?.data?.status === "INVITED_PASSWORD_SETUP_REQUIRED") {
      throw response.data;
    }
    setReadyStateFromAuth(resolveAuthResponse(response));
    logger.info("Login successful!");
    return true;
  };

  const setJwt = async (context) => {
    if (context?.userId) {
      setReadyStateFromAuth(context);
      return;
    }
    throw "No auth context in Login response!";
  };

  const resetPassword = async (requestData) => {
    await axios.post(
      endpoints.resetPassword,
      {
        email: requestData.email,
        tenantIdentifier: requestData.tenant,
        recoveryUrlBase: requestData.recoveryUrlBase,
        language: navigator.language?.startsWith("hu") ? "hu" : "en",
      },
      {
        withCredentials: true,
        headers: {
          "X-Tenant-Id": requestData.tenant,
        },
      }
    );
  };

  const confirmPasswordRecovery = async (requestData) => {
    await axios.post(endpoints.confirmPasswordRecovery, requestData, {
      withCredentials: true,
    });
  };

  const second = async (solution) => {
    const response = await axios.post(endpoints.secondFactor, solution, {
      withCredentials: true,
    });
    setReadyStateFromAuth(resolveAuthResponse(response));
    return true;
  };

  const activate = async (activation) => {
    const response = await axios.post(endpoints.activate, activation, {
      withCredentials: true,
    });
    setReadyStateFromAuth(resolveAuthResponse(response));
    return true;
  };

  const boot = (isCurrent = true) => {
    isCurrentSession.value = isCurrent;
    clearAuthContext();
    bootstrapped.value = true;
  };

  const logout = async (ids) => {
    try {
      const http = await network.connection();
      const sessionIds = Array.isArray(ids) ? ids.filter(Boolean) : [];
      if (!sessionIds.length) {
        await http.post(endpoints.logout);
        return;
      }
      await Promise.all(
        sessionIds.map((sessionId) =>
          http.post(endpoints.killSession, { sessionId })
        )
      );
    } finally {
      boot(true);
    }
  };

  const logMeOut = async () => {
    try {
      const http = await network.connection();
      await http.post(endpoints.logout);
    } finally {
      boot(true);
    }
  };

  const logMeOutEverywhere = async () => {
    try {
      const http = await network.connection();
      await http.post(endpoints.logoutAll);
    } finally {
      boot(true);
    }
  };

  const fetchSessionData = async (targetUserId = undefined) => {
    const http = await network.connection();
    const response = await http.get(endpoints.myData);
    const key = targetUserId || "CURRENT_USER";
    sessionData.value = {
      ...sessionData.value,
      [key]: response.data,
    };
  };

  const fetchAllSessionData = async () => {
    const http = await network.connection();
    const response = await http.get(endpoints.myData);
    const grouped = {};
    response.data.forEach((login) => {
      const current = grouped[login.userId] || [];
      current.push(login);
      grouped[login.userId] = current;
    });
    sessionData.value = grouped;
  };

  const updateSelectedForecastYear = (year) => {
    selectedForecastYear.value = year;
  };

  const hasAuthority = (requestedAuthority) => {
    return hasAnyAuthority(requestedAuthority)
  }

  const hasAnyAuthority = (...requestedAuthorities) => {
    const flattenedAuthorities = flattenAuthorities(requestedAuthorities)
      .map((authority) => (typeof authority === "string" ? authority.trim() : ""))
      .filter(Boolean);
    // "xxx" is the OmniAdmin privilege, which grants access to absolutely everything
    flattenedAuthorities.push("xxx");

    return flattenedAuthorities.some((authority) =>
      authorities.value.includes(authority)
    );
  };

  const relogin = () => {
    reloginRequired.value = true;
  };

  const liftRelogin = () => {
    reloginRequired.value = false;
  };

  const redirect = (url) => {
    loginRedirect.value = url;
  };

  return {
    authContext,
    jsonJwt,
    bootstrapped,
    reloginRequired,
    loginRedirect,
    sessionData,
    isCurrentSession,
    selectedForecastYear,
    isReady,
    isLoggedIn,
    userId,
    isRoot,
    displayName,
    loggingRequired,
    workScheduleMode,
    avatar,
    occupation,
    partnerId,
    authorities,
    tenantId,
    timezone,
    selectedForecastYearResolved,
    setAuthContext,
    clearAuthContext,
    bootstrap,
    refreshJwt,
    login,
    setJwt,
    resetPassword,
    confirmPasswordRecovery,
    second,
    activate,
    boot,
    logout,
    logMeOut,
    logMeOutEverywhere,
    fetchSessionData,
    fetchAllSessionData,
    updateSelectedForecastYear,
    hasAuthority,
    hasAnyAuthority,
    relogin,
    liftRelogin,
    redirect,
  };
});
